/* eslint-disable promise/always-return */
import { Anchor, Box, Button, Icon, Paragraph, Text, Toast, useToast } from '@zillow/constellation-10';
import { IconLockClosedFilled, IconSearchFilled } from '@zillow/constellation-icons';
import { RENTAL_APPLICATION_REPORTS_LAWS_URL } from '@zg-rentals/applications-utils';
import type { ApplicationData, ConditionallyApprove, EmitMetric, Log } from '../../../../models/report-models';
import { LogLevel } from '../../../../models/report-models';

const successToastBodyText = ({
  numberOfApplicants,
  applicantFirstName,
}: {
  numberOfApplicants: number | undefined;
  applicantFirstName: string | undefined;
}) => {
  return numberOfApplicants === 1
    ? `We let ${applicantFirstName} know their application was conditionally approved, pending review of the criminal history section`
    : 'We let all co-applicants know their application was conditionally approved, pending review of the criminal history section';
};

export function CriminalSectionObfuscatedContent({
  conditionallyApprove,
  isConditionallyApproving,
  emitMetric,
  log,
  applicationData,
}: {
  conditionallyApprove: ConditionallyApprove;
  isConditionallyApproving: boolean;
  emitMetric: EmitMetric;
  log: Log;
  applicationData: ApplicationData | undefined;
}) {
  const { enqueueToast } = useToast();

  function callConditionallyApprove() {
    const { applicationId, stateCode, city, numberOfApplicants, applicantFirstName } = applicationData || {};
    if (conditionallyApprove) {
      conditionallyApprove()
        .then((success) => {
          if (success) {
            emitMetric(`conditionalApprovalSuccess ${applicationData?.stateCode}`);
            log(LogLevel.Warn, {
              message: `conditionalApprovalSuccess ${stateCode}-${city} ${applicationId}`,
            });
            enqueueToast(
              <Toast tone="success">{successToastBodyText({ numberOfApplicants, applicantFirstName })}</Toast>,
            );
          } else {
            emitMetric(`conditionalApprovalFailure ${stateCode}`);
            log(LogLevel.Warn, {
              message: `conditionalApprovalFailure ${stateCode}-${city} ${applicationId}`,
            });
            enqueueToast(<Toast tone="critical">Unable to conditionally approve application</Toast>);
          }
        })
        .catch((e) => {
          emitMetric(`conditionalApprovalFailure ${stateCode}`);
          log(LogLevel.Warn, {
            message: `conditionalApprovalFailure ${stateCode}-${city} ${applicationId}`,
            error: e,
          });
          enqueueToast(<Toast tone="critical">Unable to conditionally approve application</Toast>);
        });
    } else {
      log(LogLevel.Warn, {
        message: `conditionallyApprove is undefined for ${stateCode}-${city} ${applicationId}`,
      });
    }
  }

  return (
    <Box css={{ display: 'grid', alignItems: 'center', marginBottom: 'loose' }}>
      <Box css={{ gridColumn: '1', gridRow: '1', marginLeft: 'looser', marginRight: 'looser', zIndex: 1 }}>
        <Icon size="md" css={{ color: 'text.subtle', marginBottom: 'tighter' }}>
          <IconLockClosedFilled />
        </Icon>
        <Paragraph textStyle="body-sm-bold" css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
          Do you want to move forward with this application?
        </Paragraph>
        <Paragraph textStyle="body-sm" css={{ marginBottom: 'default' }}>
          This section is hidden because your local laws require you to{' '}
          <Anchor href={RENTAL_APPLICATION_REPORTS_LAWS_URL} target="_blank">
            conditionally approve
          </Anchor>{' '}
          this application before you can view the criminal history section of the background report. If this applicant
          applied withone or more co-applicants, you’ll need to conditionally approve this application before you can
          view the criminal history on co-applicant background reports.
        </Paragraph>
        <Paragraph textStyle="body-sm" css={{ marginBottom: 'tighter' }}>
          Do you want to conditionally approve this application now?
        </Paragraph>
        <Button tone="brand" emphasis="filled" loading={isConditionallyApproving} onClick={callConditionallyApprove}>
          Yes, conditionally approve
        </Button>
      </Box>
      <Box
        css={{
          gridColumn: '1',
          gridRow: '1',
          filter: 'blur(3px)',
          opacity: 0.4,
        }}
        aria-hidden="true"
      >
        <Box css={{ marginBottom: 'loose' }}>
          <Paragraph textStyle="body-xs-bold" css={{ marginBottom: 'tighter' }}>
            Accessus Consolari
          </Paragraph>
          <Paragraph textStyle="body-sm" className="fs-exclude">
            Eget at ad oppressu voluptate me ad volutpat cicatrices. Y gloriabantur divertere y diligentissime deorum.
          </Paragraph>
        </Box>
        <Box css={{ display: 'flex', marginBottom: 'tighter' }}>
          <Text textStyle="body-sm">
            <Icon css={{ marginTop: '2px', marginRight: 'tighter' }}>
              <IconSearchFilled />
            </Icon>
          </Text>
          <Paragraph textStyle="body-sm" className="fs-exclude">
            Culpa 1 privatis malitiae dis ea sed diuturna sint mazim si V.P. nisi aequitate decembris.
          </Paragraph>
        </Box>
        <Paragraph textStyle="fineprint">
          Detrimenta Virginis, Per Privatus Quod mi Sunt Nominavit Competens. Sem capiat at mirabile deesse magnorum non
          modo respectum mi (2) libertatum attendentiam ab est feliciora nobis fallaciloquae louor nam noctis ut pede
          harum est uantum personom eos (0) regulator alterationes cum id accelerare amplissimum exclamavit id est
          habitu optandumm animalis. EUM reddite torquent quia interpres te leo plurali et observatis uidem hac axioma
          class honorem. Quo rem quae mutuum armorum est fortiumue absentia satisfactus wisi accesserunt quia rem nec
          absoluto competentes ut bcatissimae s misteria fusce uidem at leo sed quos mutare cum gloriose.
        </Paragraph>
        <Paragraph textStyle="fineprint" css={{ marginTop: 'tighter' }}>
          Moriar erat et cicatrices purus ex zzril orci eos perpetuitas.
        </Paragraph>
      </Box>
    </Box>
  );
}
